import { useEffect, useRef, useSyncExternalStore } from "react";
import { fetchAdminMe, type AdminRole } from "@/features/auth/api/adminAuthApi";
import {
  getAdminSessionFullName,
  getAdminSessionRole,
  setAdminSessionProfile,
  subscribeAdminSessionRole,
} from "@/features/auth/session/adminSessionRole";

/**
 * The logged-in admin's own role (`admin` / `super_admin`) and display name, for gating UI and
 * the top-bar account chip. Backed by `GET /admin/auth/me` — never trusted from login alone.
 *
 * Cached in sessionStorage for the tab's lifetime; cleared on sign-out (`endAdminSession`).
 */
export function useAdminRole(): {
  role: AdminRole | undefined;
  fullName: string | undefined;
  loading: boolean;
} {
  const role = useSyncExternalStore(
    subscribeAdminSessionRole,
    getAdminSessionRole,
    () => undefined,
  );
  const fullName = useSyncExternalStore(
    subscribeAdminSessionRole,
    getAdminSessionFullName,
    () => undefined,
  );
  const fetchStarted = useRef(false);

  useEffect(() => {
    if (role !== undefined || fetchStarted.current) {
      return;
    }
    fetchStarted.current = true;
    fetchAdminMe()
      .then((res) =>
        setAdminSessionProfile({
          role: res.user.role,
          fullName: res.user.full_name,
          phone: res.user.phone,
        }),
      )
      .catch(() => {
        // Leave uncached on failure so a transient error doesn't wrongly grant/deny access for
        // the rest of the session — the next mount will simply retry.
        fetchStarted.current = false;
      });
  }, [role]);

  return { role, fullName, loading: role === undefined };
}

/** Convenience wrapper for the common case: gating a single UI element on super_admin. */
export function useIsSuperAdmin(): { isSuperAdmin: boolean; loading: boolean } {
  const { role, loading } = useAdminRole();
  return { isSuperAdmin: role === "super_admin", loading };
}
